/**
 * Compact popover that lets the user pick a file path from inside the
 * Card Detail Chat tab. Reuses <FileExplorerPanel /> in picker mode —
 * directories still expand normally, but a file click resolves the
 * promise instead of opening the main viewer.
 */
import { Paperclip, X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
import { useFileExplorerStore } from '../../stores/file-explorer-store';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FileExplorerPanel } from './file-explorer-panel';

interface FilePickerPopoverProps {
  /** Called with the picked path; receives null if the user cancels. */
  onPicked: (path: string | null) => void;
  /** Optional override for the trigger button's aria-label. */
  ariaLabel?: string;
}

export function FilePickerPopover({ onPicked, ariaLabel = 'Attach file' }: FilePickerPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const openPicker = useFileExplorerStore((s) => s.openPicker);
  const cancelPicker = useFileExplorerStore((s) => s.cancelPicker);

  React.useEffect(() => {
    if (!open) return;
    openPicker((path) => {
      setOpen(false);
      onPicked(path);
    });
    return () => {
      // If the popover closes without a selection, cancel the queued
      // picker so the store doesn't hold a stale resolver.
      cancelPicker();
    };
  }, [open, openPicker, cancelPicker, onPicked]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded text-muted-foreground',
            'transition-colors hover:bg-foreground/5 hover:text-foreground',
          )}
        >
          <Paperclip className="h-3.5 w-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="h-[420px] w-[360px] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
          <span className="text-[12px] font-medium text-foreground">Attach a file</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close picker"
            className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </div>
        <div className="h-[calc(100%-32px)] overflow-hidden">
          <FileExplorerPanel pickerMode />
        </div>
      </PopoverContent>
    </Popover>
  );
}
